import { BoostModeIcon, BoostModeInactiveIcon, E3Lightbulb } from '@e3dc-react/icons'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import React from 'react'
import { TabIconButton, TabIconGroup } from 'src/Components/TabButton'
import { debounce } from 'lodash'
import { SunIcon } from 'src/Libs/flow/icons'
import { useWallboxConfigurationMutation } from 'src/Libs/wallboxes/hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

interface Props {
  isServerOffline: boolean | undefined
  isOffline: boolean | undefined
  isSunModeActive: boolean | undefined
  isBoostModeActive: boolean | undefined
  isFlowEVChargingStrategyBoostMode: boolean | undefined
  hasSmartComponent: boolean | undefined
  wallboxIndex: number
  wallboxStorageId: string | number
  isActiveChargingSession?: boolean
  isNoVehicleConnected?: boolean
}

/**
 * Component to toggle boost mode
 */
const BoostModeSwitch: React.FC<Props> = ({
  isOffline,
  isServerOffline,
  isSunModeActive,
  isBoostModeActive,
  isFlowEVChargingStrategyBoostMode,
  hasSmartComponent,
  wallboxIndex,
  wallboxStorageId,
  isActiveChargingSession,
  isNoVehicleConnected,
}) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()
  const { mutateWallboxConfiguration } = useWallboxConfigurationMutation()

  const tooltipText = getTooltipText(isServerOffline, isOffline, isNoVehicleConnected)
  const isDisabled =
    isServerOffline ||
    isOffline ||
    !isSunModeActive ||
    !hasInstallationWritingRights ||
    !isActiveChargingSession ||
    isNoVehicleConnected ||
    isFlowEVChargingStrategyBoostMode
  const defaultTabIndex = isFlowEVChargingStrategyBoostMode || isBoostModeActive ? 1 : 0

  const handleBoostModeSwitch = debounce(async (newIsBoostModeActiveValue: boolean): Promise<void> => {
    if (newIsBoostModeActiveValue === isBoostModeActive) return
    try {
      await mutateWallboxConfiguration(wallboxStorageId, wallboxIndex, { isBoostModeActive: newIsBoostModeActiveValue })
      if (newIsBoostModeActiveValue) {
        enqueueSuccessStack(t('dashboard.liveView.boostMode.success'))
      } else {
        enqueueSuccessStack(t('dashboard.liveView.boostMode.successStop'))
      }
      triggerAppRating()
    } catch (error) {
      console.error(`Error while toggling boost mode: ${error}`)
      if (newIsBoostModeActiveValue) {
        enqueueErrorStack(t('dashboard.liveView.boostMode.error'))
      } else {
        enqueueErrorStack(t('dashboard.liveView.boostMode.errorStop'))
      }
    }
  }, 300)

  return (
    <OptionalTooltipWrapper hasTooltip={isDisabled} placement="top" titleKey={tooltipText}>
      <TabIconGroup
        disabled={isDisabled}
        sx={{ cursor: isDisabled ? 'default' : 'pointer' }}
        defaultTabIndex={defaultTabIndex}
        onClick={e => e.stopPropagation()}
      >
        <TabIconButton onClick={() => handleBoostModeSwitch(false)}>{hasSmartComponent ? <E3Lightbulb /> : <SunIcon />}</TabIconButton>
        <TabIconButton onClick={() => handleBoostModeSwitch(true)}>{isSunModeActive ? <BoostModeIcon /> : <BoostModeInactiveIcon />}</TabIconButton>
      </TabIconGroup>
    </OptionalTooltipWrapper>
  )
}

export default BoostModeSwitch

const getTooltipText = (isServerOffline?: boolean, isOffline?: boolean, isNoVehicleConnected?: boolean): string | undefined => {
  if (isServerOffline) return 'dashboard.liveView.widgets.sun-mode-master-offline'

  if (isOffline) return 'dashboard.liveView.widgets.sun-mode-storage-offline'

  if (isNoVehicleConnected) return 'dashboard.liveView.widgets.sun-mode-no-vehicle'

  return undefined
}
